/** 스토어 공용 포맷 유틸 */

const krwFormatter = new Intl.NumberFormat("ko-KR");

/**
 * ISO(UTC) 일시를 주문자 시간대로 "YYYY-MM-DD HH:mm" 표시
 * 서버는 UTC 로 저장하고 레코드의 timeZone(IANA) 으로 변환해 보여준다 (의료 서버와 동일 규격).
 */
export const formatDateTimeInZone = (iso: string, timeZone: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  try {
    const parts = new Intl.DateTimeFormat("sv-SE", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
    // sv-SE 로케일은 "YYYY-MM-DD HH:mm" 형태로 출력한다
    return parts.replace(",", "");
  } catch {
    return iso.slice(0, 16).replace("T", " ");
  }
};

/** 12345 → "12,345원" */
export const formatKrw = (amount: number): string => `${krwFormatter.format(amount)}원`;

/** "01012345678" → "010-1234-5678" (표시용) */
export const formatPhoneForDisplay = (digits: string): string => {
  const cleaned = digits.replace(/\D/g, "");
  if (cleaned.length === 11) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return digits;
};

/**
 * 보호자 전화번호 마스킹 표시 — 서버 원형(E.164, 예 "+821012345919")을 "(+82) 010-****-5919" 로.
 * 국내 번호는 국가번호를 괄호로 앞에 두고 국번-****-끝4자리, 그 외는 끝 4자리만 남기고 가운데를 가린다.
 */
export const formatMaskedPhone = (raw: string | null | undefined): string => {
  if (!raw) {
    return "-";
  }
  const digits = raw.replace(/\D/g, "");
  const isKorea = raw.trim().startsWith("+82") || (digits.startsWith("82") && digits.length >= 11);
  if (isKorea) {
    const national = `0${digits.slice(2)}`; // 82 10 1234 5919 → 010 1234 5919
    if (national.length === 11) {
      return `(+82) ${national.slice(0, 3)}-****-${national.slice(-4)}`;
    }
    if (national.length === 10) {
      return `(+82) ${national.slice(0, 2)}-***-${national.slice(-4)}`;
    }
    return `(+82) ${national.slice(0, -4).replace(/\d/g, "*")}${national.slice(-4)}`;
  }
  if (digits.length >= 8) {
    const head = raw.trim().startsWith("+") ? `+${digits.slice(0, digits.length - 8)}` : digits.slice(0, digits.length - 8);
    return `${head} ****-${digits.slice(-4)}`.trim();
  }
  return "***";
};

/** 부가가치세율 (부가가치세법 제30조) — 서버(store-api computeCharge)와 같은 규칙 */
export const VAT_RATE = 0.1;

export interface ChargeBreakdown {
  /** 공급가액 = 상품 표시 가격 */
  supplyAmount: number;
  /** 부가세액 (부가세 포함 상품이면 0), 원 미만 절사 */
  vatAmount: number;
  /** 실제 청구 금액 */
  amount: number;
}

/** 표시용 청구 금액 산출 — 최종 금액은 서버(prepare 응답 amount)가 확정한다 */
export const computeCharge = (product: { price: number; vatIncluded: boolean }): ChargeBreakdown => {
  if (product.vatIncluded) {
    return { supplyAmount: product.price, vatAmount: 0, amount: product.price };
  }
  const vatAmount = Math.floor(product.price * VAT_RATE);
  return { supplyAmount: product.price, vatAmount, amount: product.price + vatAmount };
};
